import { MobileOfferCatalogEntry } from '@numeo/types'
import { useEffect, useRef, useState } from 'react'
import { mobileOfferError, mobileOffersApi } from './api'
import { clearReadyAppleBatch, PendingAppleBatch, readAppleBatch, saveAppleBatch } from './appleBatchState'
import { OfferButton, offerFieldClass, OfferNotice } from './ui'

export function AppleCodeBatch({ offer }: { offer: MobileOfferCatalogEntry }) {
    const [expiry, setExpiry] = useState('')
    const [batch, setBatch] = useState<PendingAppleBatch | null>(null)
    const [storageError, setStorageError] = useState(false)
    const [confirmed, setConfirmed] = useState(false)
    const [busy, setBusy] = useState(false)
    const [message, setMessage] = useState('')
    const [error, setError] = useState('')
    const lock = useRef(false)
    const count = offer.terms.environment === 'sandbox' ? 10 : 500
    useEffect(() => {
        try {
            const saved = readAppleBatch(offer.id)
            setBatch(saved)
            if (saved) setExpiry(saved.expiry)
        } catch {
            setStorageError(true)
            setError('Saved batch state cannot be read. Reconcile with engineering before creating codes.')
        }
    }, [offer.id])
    const generate = async () => {
        if (lock.current || !confirmed || !expiry || storageError) return
        lock.current = true
        setBusy(true)
        setError('')
        setMessage('')
        setConfirmed(false)
        try {
            const saved = readAppleBatch(offer.id)
            const expiration = new Date(`${expiry}T00:00:00Z`)
            const remaining = expiration.getTime() - Date.now()
            if (!saved && (!Number.isFinite(remaining) || remaining <= 0 || remaining > 180 * 86_400_000 || expiration.toISOString().slice(0, 10) !== expiry)) {
                setError('Choose a valid future code expiry within 180 days.')
                return
            }
            if (saved && saved.expiry !== expiry) {
                setBatch(saved)
                setExpiry(saved.expiry)
                throw new Error('Resume the saved batch before changing its expiry.')
            }
            const pending = saved || { requestId: crypto.randomUUID(), expiry, ready: false }
            // Persist before the request. An uncertain write must keep its ID AND original expiry.
            saveAppleBatch(offer.id, pending)
            setBatch(pending)
            const result = await mobileOffersApi.codes(offer.id, pending.requestId, pending.expiry)
            const ready = { ...pending, ready: result.status === 'ready' }
            saveAppleBatch(offer.id, ready)
            setBatch(ready)
            setMessage(`${result.numberOfCodes} codes imported. Allow one hour before assigning. Codes are delivered only to their assigned customer.`)
        } catch (failure) {
            setError(mobileOfferError(failure))
        } finally {
            lock.current = false
            setBusy(false)
        }
    }
    return (
        <details className="border-t border-slate-200 pt-4 dark:border-slate-700">
            <summary className="cursor-pointer text-sm font-semibold focus-visible:ring-2 focus-visible:ring-blue-600">App Store code inventory</summary>
            <div className="mt-4 space-y-3">
                <OfferNotice>
                    Offer configuration and code availability are separate. Generate or resume a batch of {count} codes; individual codes are never exposed
                    here.
                </OfferNotice>
                {batch && (
                    <OfferNotice>
                        {batch.ready
                            ? 'This browser last confirmed a completed batch. Current remaining inventory is checked by the server at assignment.'
                            : 'An unfinished batch is saved. Its expiry is locked; resume it to reconcile the outcome before creating another batch.'}
                    </OfferNotice>
                )}
                <label className="block space-y-1 text-sm font-medium">
                    Code redemption expiry
                    <input
                        type="date"
                        className={offerFieldClass}
                        value={expiry}
                        disabled={busy || !!batch || storageError}
                        onChange={(event) => {
                            setExpiry(event.target.value)
                            setConfirmed(false)
                            setMessage('')
                        }}
                    />
                </label>
                <OfferNotice>Expiry must be within 180 days. It must outlive every customer’s redeem-before deadline.</OfferNotice>
                {!batch?.ready && (
                    <>
                        <label className="flex items-start gap-2 text-sm">
                            <input
                                type="checkbox"
                                className="mt-1 h-4 w-4 accent-blue-600"
                                disabled={busy || storageError}
                                checked={confirmed}
                                onChange={(event) => setConfirmed(event.target.checked)}
                            />
                            I approve creating or resuming {count} App Store codes with this expiry.
                        </label>
                        <OfferButton disabled={busy || !expiry || !confirmed || storageError} onClick={() => void generate()}>
                            {busy ? 'Generating / reconciling…' : batch ? 'Resume saved code batch' : 'Generate code batch'}
                        </OfferButton>
                    </>
                )}
                {batch?.ready && (
                    <OfferButton
                        variant="secondary"
                        disabled={busy}
                        onClick={() => {
                            try {
                                clearReadyAppleBatch(offer.id)
                                setBatch(null)
                                setExpiry('')
                                setConfirmed(false)
                                setMessage('')
                                setError('')
                            } catch {
                                setError('An unfinished batch must be reconciled before creating another.')
                            }
                        }}
                    >
                        Prepare another batch
                    </OfferButton>
                )}
                {message && <OfferNotice>{message}</OfferNotice>}
                {error && <OfferNotice error>{error} Do not clear saved batch state to retry.</OfferNotice>}
            </div>
        </details>
    )
}
